import { useState } from 'react';
import { Paper, Stack, Text } from '@mantine/core';
import { DataTable, DataTableColumn, DataTableSortStatus } from 'mantine-datatable';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import {
  CompanySummary,
  formatGrowth,
  METRIC_LABELS,
  summariseCompanies,
  ALL_METRIC_KEYS,
  UsageMetricKey,
  UsageMetricRow,
} from './aggregate';

type SummaryPanelProps = {
  rows: UsageMetricRow[];
  extraTotalKeys: readonly UsageMetricKey[];
};

function monthLabel(monthKey: string | null): string {
  if (!monthKey) return '—';
  return new Date(`${monthKey}-01T00:00:00.000Z`).toLocaleDateString('en-GB', {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function growthSortValue(summary: CompanySummary): number {
  if (summary.growth.kind === 'percent') return summary.growth.value;
  if (summary.growth.kind === 'new') return Number.POSITIVE_INFINITY;
  return Number.NEGATIVE_INFINITY;
}

function sortValue(summary: CompanySummary, accessor: string): string | number {
  if ((ALL_METRIC_KEYS as readonly string[]).includes(accessor)) {
    return summary.totals[accessor as UsageMetricKey];
  }

  switch (accessor) {
    case 'company':
      return summary.company;
    case 'firstActiveMonth':
      return summary.firstActiveMonth ?? '';
    case 'lastActiveMonth':
      return summary.lastActiveMonth ?? '';
    case 'activeMonthsCount':
      return summary.activeMonthsCount;
    case 'grandTotal':
      return summary.grandTotal;
    case 'last3MonthsTotal':
      return summary.last3MonthsTotal;
    case 'prior3MonthsTotal':
      return summary.prior3MonthsTotal;
    case 'growth':
      return growthSortValue(summary);
    default:
      return '';
  }
}

const COLUMNS: DataTableColumn<CompanySummary>[] = [
  { accessor: 'company', title: 'Company', sortable: true },
  {
    accessor: 'firstActiveMonth',
    title: 'First active',
    sortable: true,
    render: row => monthLabel(row.firstActiveMonth),
  },
  {
    accessor: 'lastActiveMonth',
    title: 'Last active',
    sortable: true,
    render: row => monthLabel(row.lastActiveMonth),
  },
  { accessor: 'activeMonthsCount', title: 'Active months', sortable: true, textAlign: 'right' },
  ...ALL_METRIC_KEYS.map(
    (key): DataTableColumn<CompanySummary> => ({
      accessor: key,
      title: METRIC_LABELS[key],
      sortable: true,
      textAlign: 'right',
      render: row => row.totals[key],
    }),
  ),
  { accessor: 'grandTotal', title: 'Grand total', sortable: true, textAlign: 'right' },
  { accessor: 'last3MonthsTotal', title: 'Last 3 months', sortable: true, textAlign: 'right' },
  { accessor: 'prior3MonthsTotal', title: 'Prior 3 months', sortable: true, textAlign: 'right' },
  {
    accessor: 'growth',
    title: '3-month growth',
    sortable: true,
    textAlign: 'right',
    render: row => formatGrowth(row.growth),
  },
];

export default function SummaryPanel({ rows, extraTotalKeys }: SummaryPanelProps) {
  const summaries = summariseCompanies(rows, extraTotalKeys);
  const [sortStatus, setSortStatus] = useState<DataTableSortStatus<CompanySummary>>({
    columnAccessor: 'grandTotal',
    direction: 'desc',
  });

  const direction = sortStatus.direction === 'asc' ? 1 : -1;
  const sorted = [...summaries].sort((a, b) => {
    const aValue = sortValue(a, sortStatus.columnAccessor);
    const bValue = sortValue(b, sortStatus.columnAccessor);
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return aValue.localeCompare(bValue) * direction;
    }
    return ((aValue as number) - (bValue as number)) * direction;
  });

  return (
    <Paper withBorder p="md" radius="md">
      <Stack gap="sm">
        <Text size="sm" fw={FONT_WEIGHTS.semibold}>
          Company summary
        </Text>
        <DataTable<CompanySummary>
          records={sorted}
          columns={COLUMNS}
          sortStatus={sortStatus}
          onSortStatusChange={setSortStatus}
          withTableBorder={false}
          withColumnBorders
          striped
          highlightOnHover
          minHeight={sorted.length === 0 ? 120 : undefined}
          noRecordsText="No companies with usage yet."
          fz="xs"
        />
      </Stack>
    </Paper>
  );
}
